/**
 * Normalizes any CSS color string (hex, RGB, RGBA) to a 6-digit hex string.
 * Returns the original string if already hex, or converts RGB(a) to hex.
 * Falls back to null if unparseable.
 */
export const normalizeColor = (color) => {
  if (!color || typeof color !== "string") return null;

  const trimmed = color.trim();

  // Already hex
  if (/^#?[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  }

  // RGB or RGBA: rgb(r,g,b) / rgba(r,g,b,a)
  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
  );
  if (rgbMatch) {
    const toHex = (v) =>
      Math.min(255, Math.max(0, parseInt(v, 10)))
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }

  return null;
};

/**
 * Generates a random dark hex color (approximately 66% darkness)
 * @returns {string} A hex color string (e.g., "#1a2b3c")
 */
export const generateRandomDarkColor = (darkness = 0.5) => {
  // Generate RGB values in the darker range (0-85 for ~66% darkness)
  // 85/255 ≈ 0.33, so this gives us colors that are roughly 67% dark
  const max = 255 * darkness;

  const r = Math.floor(Math.random() * max); // 0-85
  const g = Math.floor(Math.random() * max); // 0-85
  const b = Math.floor(Math.random() * max); // 0-85

  // Convert to hex and pad with zeros if needed
  const toHex = (value) => value.toString(16).padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Determines if a hex color is dark (66% or darker)
 * Uses weighted luminance calculation for better perceptual accuracy
 * @param {string} hexColor - Hex color string (with or without #)
 * @returns {boolean} True if the color is dark, false otherwise
 */
export const isDarkColor = (colorInput) => {
  const normalized = normalizeColor(colorInput);
  if (!normalized) return true;

  const hex = normalized.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  const brightnessThreshold = 255 * 0.5;

  return luminance < brightnessThreshold;
};

/**
 * Alternative simpler darkness check using average RGB values
 * @param {string} hexColor - Hex color string (with or without #)
 * @returns {boolean} True if the color is dark, false otherwise
 */
export const isDarkColorSimple = (colorInput) => {
  const normalized = normalizeColor(colorInput);
  if (!normalized) return true;

  const hex = normalized.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const average = (r + g + b) / 3;
  return average < 255 * 0.34;
};

export const getColorGradient = (colorInput, lightenAmount = 25) => {
  const color = normalizeColor(colorInput) || "#000000";
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Create a very mild gradient by lightening the color slightly
  const lighterR = Math.min(255, r + lightenAmount);
  const lighterG = Math.min(255, g + lightenAmount);
  const lighterB = Math.min(255, b + lightenAmount);

  // Convert back to hex
  const toHex = (value) => value.toString(16).padStart(2, "0");
  const lighterColor = `#${toHex(lighterR)}${toHex(lighterG)}${toHex(
    lighterB
  )}`;

  const gradient = `linear-gradient(135deg, ${color}, ${lighterColor})`;

  return gradient;
};

export const defaultColorMap = {
  "#E11D48": "#FFFFFF",
  "#EC4899": "#FFFFFF",
  "#8B5CF6": "#FFFFFF",
  "#3B82F6": "#FFFFFF",
  "#67E8F9": "#000000",
  "#0D9488": "#FFFFFF",
  "#7DC240": "#FFFFFF",
  "#F59E0B": "#FFFFFF",
  "#F97316": "#FFFFFF",
  "#6C786D": "#FFFFFF",
  "#B8A383": "#FFFFFF",
  "#000000": "#FFFFFF",
  "#FBCFE8": "#9D174D",
  "#DDD6FE": "#5B21B6",
  "#DBEAFE": "#1D4ED8",
  "#99F6E4": "#115E59",
  "#B6E490": "#30471D",
  "#FCD34D": "#92400E",
  "#FED7AA": "#C2410C",
  "#E5DFD0": "#3B352B",
};
